import {
  Alert, App as AntApp, Badge, Button, Card, Col, Empty, Form, Input, InputNumber, List, Popconfirm, Row, Space, Tag,
  Typography,
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, errorText } from "../api";
import { dateTime } from "../format";
import { canManage, useSession } from "../session";
import { useResource } from "../useResource";

/** `DeskConsoleController.item` */
export interface DeskItem {
  handoffId: string;
  conversationId: string;
  reason: string;
  status: "WAITING" | "ACCEPTED" | "TIMED_OUT" | "CANCELLED" | "RELEASED" | "CLOSED";
  requestedAt: string;
  deadlineAt: string;
  waitSeconds: number;
  assignedUserId: string | null;
  lastCustomerText?: string;
  lastCustomerAt?: string | null;
}

export interface DeskOverview {
  waiting: DeskItem[];
  mine: DeskItem[];
  others?: DeskItem[];
  userId: string;
}

export interface DeskEvent {
  seq: number;
  type: string;
  actor: string;
  createdAt: string;
  text?: string;
  products?: { sku: string; name: string; priceText: string }[];
  from?: string;
  to?: string;
  sku?: string;
  quantity?: number;
  added?: boolean;
  reason?: string;
}

export interface DeskConversation {
  conversationId: string;
  mode: "AI" | "WAITING_HUMAN" | "HUMAN" | "CLOSED";
  handoff: DeskItem;
  events: DeskEvent[];
}

const QUEUE_REFRESH_MS = 5000;
const CONVERSATION_REFRESH_MS = 3000;

/** Periyodik yenileme: zamanlayıcı yalnızca `reload` çağırır (state değişikliği istek tamamlanınca olur). */
function useRefresh(reload: () => void, intervalMs: number) {
  useEffect(() => {
    const id = window.setInterval(reload, intervalMs);
    return () => window.clearInterval(id);
  }, [reload, intervalMs]);
}

export function waitText(seconds: number): string {
  if (seconds < 60) return `${seconds} sn`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} dk ${seconds % 60} sn`;
}

/**
 * Satış Masası (v0.9 §7.2, paket 3.3). Bekleyen görüşmeler, devralma, görüşme geçmişi ve müşteriye yazma. Devralma
 * atomiktir: aynı görüşmeyi başka bir temsilci aldıysa uyarı gösterilir. Liste ve görüşme birkaç saniyede bir yenilenir.
 */
export function DeskPage() {
  const me = useSession((s) => s.me);
  const { message } = AntApp.useApp();
  const [selected, setSelected] = useState<string | null>(null);
  const { data, error, reload } = useResource(useCallback(() => api.get<DeskOverview>("/api/console/desk"), []));
  useRefresh(reload, QUEUE_REFRESH_MS);

  async function accept(item: DeskItem) {
    try {
      await api.post(`/api/console/desk/handoffs/${item.handoffId}/accept`);
      message.success("Görüşme sizde");
      setSelected(item.conversationId);
    } catch (e) {
      message.error(errorText(e));
    } finally {
      reload();
    }
  }

  if (error && !data) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!data) {
    return null;
  }
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Satış Masası</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} lg={9}>
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Card title={<Space>Bekleyenler<Badge count={data.waiting.length} showZero color="blue" /></Space>}>
              <List<DeskItem> dataSource={data.waiting} locale={{ emptyText: "Bekleyen görüşme yok" }}
                renderItem={(item) => (
                  <List.Item actions={[
                    <Button key="view" size="small" onClick={() => setSelected(item.conversationId)}>Göster</Button>,
                    <Button key="accept" size="small" type="primary" onClick={() => accept(item)}>Devral</Button>,
                  ]}>
                    <List.Item.Meta title={<Space>{`${waitText(item.waitSeconds)} bekliyor`}
                      {new Date(item.deadlineAt) < new Date() && <Tag color="red">Süre doldu</Tag>}</Space>}
                      description={item.lastCustomerText ?? "—"} />
                  </List.Item>
                )} />
            </Card>
            <Card title="Benim görüşmelerim">
              <List<DeskItem> dataSource={data.mine} locale={{ emptyText: "Aktif görüşmeniz yok" }}
                renderItem={(item) => (
                  <List.Item actions={[
                    <Button key="open" size="small" type={selected === item.conversationId ? "primary" : "default"}
                      onClick={() => setSelected(item.conversationId)}>Aç</Button>,
                  ]}>
                    <List.Item.Meta title={item.lastCustomerText ?? "Görüşme"}
                      description={`Devralındı · ${waitText(item.waitSeconds)} bekledi`} />
                  </List.Item>
                )} />
            </Card>
            {data.others && (
              <Card title="Diğer temsilcilerdeki görüşmeler">
                <List<DeskItem> dataSource={data.others} locale={{ emptyText: "Yok" }}
                  renderItem={(item) => (
                    <List.Item actions={[
                      <Button key="view" size="small" onClick={() => setSelected(item.conversationId)}>Göster</Button>,
                    ]}>
                      <List.Item.Meta title={item.lastCustomerText ?? "Görüşme"}
                        description={`Temsilci #${item.assignedUserId ?? "?"}`} />
                    </List.Item>
                  )} />
              </Card>
            )}
          </Space>
        </Col>
        <Col xs={24} lg={15}>
          {selected
            ? <ConversationPanel key={selected} conversationId={selected} userId={data.userId}
              onChanged={reload} onClosed={() => { setSelected(null); reload(); }} />
            : <Card><Empty description="Bir görüşme seçin" /></Card>}
        </Col>
      </Row>
      {canManage(me) && <DeskSettingsCard />}
    </Space>
  );
}

function ConversationPanel({ conversationId, userId, onChanged, onClosed }: {
  conversationId: string; userId: string; onChanged: () => void; onClosed: () => void;
}) {
  const { message } = AntApp.useApp();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const { data, error, reload } = useResource(useCallback(
    () => api.get<DeskConversation>(`/api/console/desk/conversations/${conversationId}`), [conversationId]));
  useRefresh(reload, CONVERSATION_REFRESH_MS);
  const end = useRef<HTMLDivElement>(null);
  const count = data?.events.length ?? 0;
  useEffect(() => end.current?.scrollIntoView?.({ block: "end" }), [count]);

  const mine = data?.mode === "HUMAN" && data.handoff.status === "ACCEPTED" && data.handoff.assignedUserId === userId;

  async function send() {
    const value = text.trim();
    if (!value) return;
    setSending(true);
    try {
      await api.post(`/api/console/desk/conversations/${conversationId}/messages`, { text: value },
        { "Idempotency-Key": `rep-${crypto.randomUUID()}` });
      setText("");
      reload();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSending(false);
    }
  }

  async function finish(action: "release" | "close") {
    try {
      await api.post(`/api/console/desk/conversations/${conversationId}/${action}`);
      message.success(action === "release" ? "Görüşme AI asistana bırakıldı" : "Görüşme kapatıldı");
      onClosed();
    } catch (e) {
      message.error(errorText(e));
      onChanged();
    }
  }

  if (error && !data) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!data) {
    return null;
  }
  return (
    <Card title={<Space>Görüşme<Tag>{MODES[data.mode]}</Tag></Space>}
      extra={mine && (
        <Space>
          <Popconfirm title="Görüşme AI asistana bırakılsın mı?" okText="Bırak" cancelText="Vazgeç"
            onConfirm={() => finish("release")}>
            <Button>AI'ya bırak</Button>
          </Popconfirm>
          <Popconfirm title="Görüşme kapatılsın mı?" description="Müşteri bu konuşmaya yazamaz." okText="Kapat"
            cancelText="Vazgeç" onConfirm={() => finish("close")}>
            <Button danger>Kapat</Button>
          </Popconfirm>
        </Space>
      )}>
      <div style={{ maxHeight: 480, overflowY: "auto", marginBottom: 16 }} role="log" tabIndex={0}
        aria-label="Görüşme geçmişi">
        {data.events.map((e) => <EventLine key={e.seq} event={e} />)}
        <div ref={end} />
      </div>
      {mine ? (
        <Space.Compact style={{ width: "100%" }}>
          <Input.TextArea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
            autoSize={{ minRows: 1, maxRows: 4 }} aria-label="Müşteriye mesaj" placeholder="Müşteriye yazın"
            onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); void send(); } }} />
          <Button type="primary" loading={sending} onClick={() => void send()}>Gönder</Button>
        </Space.Compact>
      ) : (
        <Typography.Text type="secondary">
          {data.mode === "WAITING_HUMAN" ? "Yazmak için görüşmeyi devralın." : "Bu görüşmeye yazamazsınız."}
        </Typography.Text>
      )}
    </Card>
  );
}

const MODES: Record<DeskConversation["mode"], string> = {
  AI: "AI asistan", WAITING_HUMAN: "Temsilci bekliyor", HUMAN: "Temsilcide", CLOSED: "Kapalı",
};

const ACTORS: Record<string, string> = { CUSTOMER: "Müşteri", AI: "AI asistan", SALES_REP: "Temsilci", SYSTEM: "Sistem" };

function EventLine({ event }: { event: DeskEvent }) {
  const time = <Typography.Text type="secondary" style={{ fontSize: 12 }}>{dateTime(event.createdAt)}</Typography.Text>;
  switch (event.type) {
    case "user_message":
    case "assistant_message":
    case "human_message":
      return (
        <div style={{ marginBottom: 12 }}>
          <Space size={8}><Typography.Text strong>{ACTORS[event.actor] ?? event.actor}</Typography.Text>{time}</Space>
          <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 4 }}>{event.text}</Typography.Paragraph>
          {event.products && event.products.length > 0 && (
            <Space wrap>{event.products.map((p) => <Tag key={p.sku}>{p.name} · {p.priceText}</Tag>)}</Space>
          )}
        </div>
      );
    case "mode_changed":
      return <Notice time={time} text={`${MODES[event.to as DeskConversation["mode"]] ?? event.to}`} />;
    case "cart_intent":
      return <Notice time={time} text={`Sepete ekleme isteği: ${event.sku ?? ""} × ${event.quantity ?? 1}`} />;
    case "cart_result":
      return <Notice time={time} text={event.added ? "Ürün sepete eklendi" : "Ürün sepete eklenemedi"} />;
    case "handoff_unavailable":
      return <Notice time={time} text={event.reason === "timeout" ? "Temsilci bulunamadı (süre doldu)" : "Mesai dışı"} />;
    default:
      return null;
  }
}

function Notice({ text, time }: { text: string; time: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12, textAlign: "center" }}>
      <Typography.Text type="secondary">{text}</Typography.Text> {time}
    </div>
  );
}

const DAYS: { key: string; label: string }[] = [
  { key: "MONDAY", label: "Pazartesi" }, { key: "TUESDAY", label: "Salı" }, { key: "WEDNESDAY", label: "Çarşamba" },
  { key: "THURSDAY", label: "Perşembe" }, { key: "FRIDAY", label: "Cuma" }, { key: "SATURDAY", label: "Cumartesi" },
  { key: "SUNDAY", label: "Pazar" },
];

interface DeskSettings {
  timeoutSeconds: number;
  businessHours: Record<string, [string, string]>;
}

type SettingsForm = { timeoutSeconds: number } & Record<string, string | number | undefined>;

const HOUR = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Bekleme süresi ve mesai saatleri (tenant saat dilimi). Boş bırakılan gün kapalıdır; hiç gün yoksa her zaman açık. */
function DeskSettingsCard() {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<SettingsForm>();
  const [saving, setSaving] = useState(false);
  const { data } = useResource(useCallback(async () => {
    const s = await api.get<DeskSettings>("/api/console/desk/settings");
    const values: SettingsForm = { timeoutSeconds: s.timeoutSeconds };
    for (const d of DAYS) {
      values[`${d.key}_open`] = s.businessHours[d.key]?.[0];
      values[`${d.key}_close`] = s.businessHours[d.key]?.[1];
    }
    form.setFieldsValue(values);
    return s;
  }, [form]));

  async function save(values: SettingsForm) {
    const businessHours: Record<string, [string, string]> = {};
    for (const d of DAYS) {
      const open = values[`${d.key}_open`];
      const close = values[`${d.key}_close`];
      if (typeof open === "string" && open && typeof close === "string" && close) {
        businessHours[d.key] = [open, close];
      }
    }
    setSaving(true);
    try {
      await api.put("/api/console/desk/settings", { timeoutSeconds: values.timeoutSeconds, businessHours });
      message.success("Satış Masası ayarları kaydedildi");
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Satış Masası ayarları">
      {data && (
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item label="Temsilci bekleme süresi (saniye)" name="timeoutSeconds" rules={[{ required: true }]}
            tooltip="Bu sürede temsilci bulunamazsa müşteriye AI ile devam etme veya kapatma seçenekleri sunulur">
            <InputNumber min={30} max={3600} />
          </Form.Item>
          <Typography.Paragraph type="secondary">
            Mesai saatleri mağazanın saat dilimindedir. Boş bırakılan gün kapalıdır; hiçbir gün girilmezse Satış Masası
            her zaman açık sayılır.
          </Typography.Paragraph>
          {DAYS.map((d) => (
            <Space key={d.key} align="baseline" style={{ display: "flex" }}>
              <Typography.Text style={{ width: 90, display: "inline-block" }}>{d.label}</Typography.Text>
              <Form.Item name={`${d.key}_open`} rules={[{ pattern: HOUR, message: "SS:DD" }]}>
                <Input placeholder="09:00" style={{ width: 90 }} aria-label={`${d.label} açılış`} />
              </Form.Item>
              <Form.Item name={`${d.key}_close`} rules={[{ pattern: HOUR, message: "SS:DD" }]}>
                <Input placeholder="18:00" style={{ width: 90 }} aria-label={`${d.label} kapanış`} />
              </Form.Item>
            </Space>
          ))}
          <Button type="primary" htmlType="submit" loading={saving}>Kaydet</Button>
        </Form>
      )}
    </Card>
  );
}
